/**
 * AdvertiserSignupPage — advertiser self-signup review console.
 *
 * Ported from legacy apps/dash-legacy/src/routes/_authed/settings/advertiser-signup.tsx,
 * modernized per spec: table (Name, Contact, Vertical, Status, Created) + status filter
 * (pending/approved/rejected/all) + Review drawer showing submitted form fields with
 * Approve/Reject action and reason dropdown.
 *
 * Legacy concepts reused: emerald/sky accent palette, zinc borders + 6-8px rounding,
 * plain Tailwind + light theme (no dark mode, no lucide-react icons, no shadcn).
 */
import { useMemo, useState } from "react";
import {
	useAdvertiserSignupList,
	useAdvertiserSignupReview,
} from "./hooks";
import type {
	AdvertiserSignupRow,
	AdvertiserSignupStatus,
} from "./api";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-sm";
const BTN =
	"rounded-[6px] border border-sky-600 bg-sky-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-rose-600 bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60";
const SELECT =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1.5 text-sm text-zinc-900 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20";

const STATUS_FILTERS: Array<{ value: AdvertiserSignupStatus | "all"; label: string }> = [
	{ value: "all", label: "All" },
	{ value: "pending", label: "Pending" },
	{ value: "approved", label: "Approved" },
	{ value: "rejected", label: "Rejected" },
];

const REJECT_REASONS = [
	"Insufficient company information",
	"Vertical not supported",
	"Suspected fraud",
	"Budget below minimum",
	"Other",
];

function statusPill(status: AdvertiserSignupStatus | undefined) {
	const s = status ?? "pending";
	const styles: Record<AdvertiserSignupStatus, string> = {
		pending: "bg-amber-50 text-amber-700 border-amber-200",
		approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
		rejected: "bg-rose-50 text-rose-700 border-rose-200",
	};
	return (
		<span
			data-testid={`advertiser-signup-status-${s}`}
			className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium capitalize ${styles[s]}`}
		>
			{s}
		</span>
	);
}

function formatDate(ts?: number): string {
	if (!ts) return "-";
	try {
		const d = new Date(ts);
		if (Number.isNaN(d.getTime())) return "-";
		return d.toLocaleDateString(undefined, {
			year: "numeric",
			month: "short",
			day: "numeric",
		});
	} catch {
		return "-";
	}
}

function ReviewDrawer({
	row,
	onClose,
}: {
	row: AdvertiserSignupRow;
	onClose: () => void;
}) {
	const reviewMut = useAdvertiserSignupReview();
	const [reason, setReason] = useState<string>(REJECT_REASONS[0]);

	const fields: Array<{ label: string; value: unknown }> = [
		{ label: "Company", value: row.company_name },
		{ label: "Contact", value: row.contact_name },
		{ label: "Email", value: row.email },
		{ label: "Phone", value: row.phone },
		{ label: "Country", value: row.country },
		{ label: "Website", value: row.website },
		{ label: "Vertical", value: row.vertical },
		{
			label: "Monthly budget (USD)",
			value:
				typeof row.monthly_budget_usd === "number"
					? `$${row.monthly_budget_usd.toLocaleString()}`
					: row.monthly_budget_usd,
		},
		{ label: "Notes", value: row.notes },
	];

	function handleApprove() {
		reviewMut.mutate(
			{ id: row.id, body: { status: "approved" } },
			{ onSuccess: onClose },
		);
	}
	function handleReject() {
		reviewMut.mutate(
			{ id: row.id, body: { status: "rejected", reason } },
			{ onSuccess: onClose },
		);
	}

	return (
		<div
			data-testid="advertiser-signup-drawer"
			className="fixed inset-0 z-40 flex"
			role="dialog"
			aria-modal="true"
			aria-label="Review advertiser signup"
		>
			<button
				type="button"
				aria-label="Close drawer"
				onClick={onClose}
				className="absolute inset-0 bg-zinc-900/30"
				data-testid="advertiser-signup-drawer-backdrop"
			/>
			<aside className="relative ml-auto flex h-full w-full max-w-lg flex-col overflow-y-auto bg-white shadow-xl">
				<header className="flex items-start justify-between border-b border-zinc-200 px-6 py-4">
					<div>
						<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
							Advertiser signup
						</p>
						<h2 className="mt-1 text-lg font-semibold text-zinc-900">
							{row.company_name ?? "Untitled advertiser"}
						</h2>
						<div className="mt-2">{statusPill(row.status)}</div>
					</div>
					<button
						type="button"
						onClick={onClose}
						className={BTN_GHOST}
						data-testid="advertiser-signup-drawer-close"
					>
						Close
					</button>
				</header>

				<section className="flex-1 px-6 py-5">
					<h3 className="text-sm font-semibold text-zinc-900">
						Submitted details
					</h3>
					<dl className="mt-3 divide-y divide-zinc-100 rounded-[6px] border border-zinc-200 bg-zinc-50/40">
						{fields.map((f) => (
							<div
								key={f.label}
								className="grid grid-cols-3 gap-3 px-3 py-2 text-sm"
							>
								<dt className="col-span-1 font-medium text-zinc-600">
									{f.label}
								</dt>
								<dd className="col-span-2 text-zinc-900">
									{f.value === undefined ||
									f.value === null ||
									f.value === ""
										? (
											<span className="text-zinc-400">-</span>
											)
										: String(f.value)}
								</dd>
							</div>
						))}
					</dl>

					{row.review_reason ? (
						<div className="mt-4 rounded-[6px] border border-zinc-200 bg-zinc-50 p-3 text-sm">
							<p className="font-medium text-zinc-700">Previous review reason</p>
							<p className="mt-1 text-zinc-600">{row.review_reason}</p>
						</div>
					) : null}
				</section>

				<footer className="border-t border-zinc-200 px-6 py-4">
					<div className="flex flex-col gap-3">
						<label className="flex flex-col gap-1 text-xs font-medium text-zinc-700">
							Rejection reason (used only when rejecting)
							<select
								value={reason}
								onChange={(e) => setReason(e.currentTarget.value)}
								className={SELECT}
								data-testid="advertiser-signup-reject-reason"
								disabled={reviewMut.isPending}
							>
								{REJECT_REASONS.map((r) => (
									<option key={r} value={r}>
										{r}
									</option>
								))}
							</select>
						</label>
						<div className="flex items-center justify-end gap-2">
							<button
								type="button"
								className={BTN_DANGER}
								onClick={handleReject}
								disabled={reviewMut.isPending}
								data-testid="advertiser-signup-reject"
							>
								{reviewMut.isPending ? "Saving." : "Reject"}
							</button>
							<button
								type="button"
								className={BTN}
								onClick={handleApprove}
								disabled={reviewMut.isPending}
								data-testid="advertiser-signup-approve"
							>
								{reviewMut.isPending ? "Saving." : "Approve"}
							</button>
						</div>
						{reviewMut.error ? (
							<p
								className="text-xs text-rose-600"
								role="alert"
								data-testid="advertiser-signup-review-error"
							>
								{reviewMut.error.message}
							</p>
						) : null}
					</div>
				</footer>
			</aside>
		</div>
	);
}

export function AdvertiserSignupPage() {
	const [statusFilter, setStatusFilter] =
		useState<AdvertiserSignupStatus | "all">("pending");
	const [selectedId, setSelectedId] = useState<string | null>(null);

	const listQuery = useAdvertiserSignupList(statusFilter);

	const items = useMemo(() => listQuery.data?.items ?? [], [listQuery.data]);
	const selectedRow = useMemo(
		() => items.find((r) => r.id === selectedId) ?? null,
		[items, selectedId],
	);

	return (
		<main
			data-testid="advertiser-signup-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					advertiser-signup
				</p>
				<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent">
					Advertiser Signup Review
				</h1>
				<p className="text-sm text-zinc-600">
					Review advertiser self-signup submissions. Approve to activate the
					account, or reject with a reason.
				</p>
			</header>

			<section className={CARD} aria-label="Advertiser signup submissions">
				<div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-200 px-5 py-3">
					<div className="flex items-center gap-2">
						<label
							htmlFor="advertiser-signup-status-filter"
							className="text-xs font-semibold uppercase tracking-wider text-zinc-500"
						>
							Status
						</label>
						<select
							id="advertiser-signup-status-filter"
							value={statusFilter}
							onChange={(e) =>
								setStatusFilter(
									e.currentTarget.value as AdvertiserSignupStatus | "all",
								)
							}
							className={SELECT}
							data-testid="advertiser-signup-filter"
						>
							{STATUS_FILTERS.map((s) => (
								<option key={s.value} value={s.value}>
									{s.label}
								</option>
							))}
						</select>
					</div>
					<p className="text-xs text-zinc-500">
						{listQuery.isLoading ? "Loading." : `${items.length} shown`}
					</p>
				</div>

				{listQuery.isLoading ? (
					<p
						className="px-5 py-8 text-sm text-zinc-500"
						data-testid="advertiser-signup-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p
						className="px-5 py-8 text-sm text-rose-600"
						role="alert"
						data-testid="advertiser-signup-error"
					>
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<p
						className="px-5 py-8 text-sm text-zinc-500"
						data-testid="advertiser-signup-empty"
					>
						No submissions match this filter yet.
					</p>
				) : (
					<div className="overflow-x-auto">
						<table
							className="w-full text-left text-sm"
							data-testid="advertiser-signup-list"
						>
							<thead className="border-b border-zinc-200 bg-zinc-50/60 text-xs uppercase tracking-wider text-zinc-500">
								<tr>
									<th className="px-5 py-2 font-semibold">Name</th>
									<th className="px-5 py-2 font-semibold">Contact</th>
									<th className="px-5 py-2 font-semibold">Vertical</th>
									<th className="px-5 py-2 font-semibold">Status</th>
									<th className="px-5 py-2 font-semibold">Created</th>
									<th className="px-5 py-2 font-semibold text-right">
										Action
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-100">
								{items.map((row) => (
									<tr
										key={row.id}
										className="hover:bg-zinc-50/60"
										data-testid={`advertiser-signup-row-${row.id}`}
									>
										<td className="px-5 py-2.5 font-medium text-zinc-900">
											{row.company_name ?? (
												<span className="text-zinc-400">Unnamed</span>
											)}
										</td>
										<td className="px-5 py-2.5 text-zinc-700">
											<div className="flex flex-col">
												<span>
													{row.contact_name ?? (
														<span className="text-zinc-400">-</span>
													)}
												</span>
												<span className="text-xs text-zinc-500">
													{row.email ?? ""}
												</span>
											</div>
										</td>
										<td className="px-5 py-2.5 text-zinc-700">
											{row.vertical ?? (
												<span className="text-zinc-400">-</span>
											)}
										</td>
										<td className="px-5 py-2.5">{statusPill(row.status)}</td>
										<td className="px-5 py-2.5 text-zinc-600">
											{formatDate(row.created_at)}
										</td>
										<td className="px-5 py-2.5 text-right">
											<button
												type="button"
												className={BTN_GHOST}
												onClick={() => setSelectedId(row.id)}
												data-testid={`advertiser-signup-review-${row.id}`}
											>
												Review
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>

			{selectedRow ? (
				<ReviewDrawer
					row={selectedRow}
					onClose={() => setSelectedId(null)}
				/>
			) : null}
		</main>
	);
}

export default AdvertiserSignupPage;
